// src/components/settings/AutomationSettings.js

/* global __firebase_config, __initial_auth_token */ // <-- Keep this linter rule

import React, { useState, useEffect } from 'react';
// --- FIX: Import the *existing* db instance ---
import { db } from '../../firebase/config'; 
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';
// PHASE FO-2 (Field Ops roadmap, Routing Cut) VERIFIER: r1-fo-autosettings-import-5a9c2e71
import { ROLES } from '../../utils/permissions';

// --- REMOVED ALL THE 'initializeApp', 'getAuth', etc. imports ---

// a1-ownedkeys-3f7b2e94 — the ONLY keys Save writes, each at its own dotted
// path (`automationSettings.<key>`). Save used to write the whole map with
// `updateDoc(orgRef, { automationSettings: settings })`, which REPLACES the map:
// any key not in local state was deleted, and any key another admin had changed
// since this screen loaded was reverted. Eight of these are read only by the
// backend (scheduledInvoiceProcessor, completeJob) and four only by mobile
// price-review routing, so a wrong value here is silent — nothing in the UI
// shows it. Dotted paths mean this screen can only ever touch what it owns.
const AUTOMATION_FIELDS = [
    'remindersEnabled', 'reminderStartDays', 'reminderIntervalDays', 'reminderEmailBody',
    'lateFeesEnabled', 'lateFeeAmount', 'lateFeeDays', 'lateFeeType',
    'fieldTechInvoiceEnabled', 'trackLaborTime', 'billLaborToCustomer',
    'onSiteReviewAssigneeId', 'onSiteReviewAssigneeName',
    'onSiteReviewAssigneeIds', 'onSiteReviewAssigneeNames',
];

// a1-ownedkeys-3f7b2e94 — lifted out of useState so the load effect can reset to
// it on an org switch (God mode switches org in place without remounting).
const INITIAL_AUTOMATION_SETTINGS = {
    remindersEnabled: false,
    reminderStartDays: 3,
    reminderIntervalDays: 7,
    reminderEmailBody: `Hello {customerName},\n\nThis is a friendly reminder that invoice #{invoiceNumber} was due on {dueDate} and is now overdue.\n\nPlease make a payment at your earliest convenience.\n\nThank you,\n{companyName}`,
    lateFeesEnabled: false,
    lateFeeAmount: 0,
    lateFeeDays: 7,
    lateFeeType: 'Fixed',
    fieldTechInvoiceEnabled: false,
    // r5-b2b-switches-state-5b1e9c74 — Labor billing switches (Increment 1, inert)
    trackLaborTime: false,      // Switch 1: track labor time on jobs (cost / profitability)
    billLaborToCustomer: false, // Switch 2 (org default): bill tracked labor to customers
    // PHASE FO-2 VERIFIER: r1-fo-autosettings-state-5a9c2e71
    // On-site item pricing review assignee. Empty string = falls back to
    // the org owner at flag-fire time (mobile handler resolves the owner).
    onSiteReviewAssigneeId: '',
    onSiteReviewAssigneeName: '',
    // PHASE FO-4 (Multi-reviewer) VERIFIER: r1-fo-autosettings-multistate-5a9c2e71
    // Multi-reviewer: arrays of UIDs + parallel names. Supersede the single
    // fields above (kept for back-compat read of older saved configs).
    onSiteReviewAssigneeIds: [],
    onSiteReviewAssigneeNames: [],
};

const AutomationSettings = ({ currentUserProfile }) => {
    const [settings, setSettings] = useState(INITIAL_AUTOMATION_SETTINGS);

    const [currency, setCurrency] = useState('USD');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    // a1-loadfail-6c2d9a15 — set when the org read fails. The screen previously
    // cleared isLoading in the catch, so a permission-denied or offline read
    // rendered the form populated with the hardcoded defaults above — where
    // remindersEnabled is false — and one Save wrote that whole default map over
    // a live config. For an org with dunning switched on, that silently stops
    // every overdue-invoice reminder. Fail closed instead.
    const [loadError, setLoadError] = useState(false);
    // PHASE FO-2 VERIFIER: r1-fo-autosettings-userstate-5a9c2e71
    // Invoicing-capable org users for the review-assignee picker.
    const [reviewAssigneeOptions, setReviewAssigneeOptions] = useState([]);
    
    // --- REMOVED: const [db, setDb] = useState(null); ---
    // --- REMOVED: const [auth, setAuth] = useState(null); ---

    const orgId = currentUserProfile?.organizationId;

    // --- REMOVED: The entire first useEffect that called initializeApp ---

    // --- UPDATED: This effect now runs on mount (since 'db' is imported) ---
    useEffect(() => {
        if (!orgId) {
            setIsLoading(false); // No orgId, so nothing to load
            return;
        }

        setIsLoading(true);
        // a1-ownedkeys-3f7b2e94 — start from defaults whenever the org changes, so
        // nothing loaded from a previously viewed org (God mode switches org in
        // place, without remounting) survives in state and gets saved into this
        // one. Without this the `...prev` spread below carries the old org's
        // reminderEmailBody into any org that has none.
        setSettings(INITIAL_AUTOMATION_SETTINGS);
        setLoadError(false);
        const orgRef = doc(db, 'organizations', orgId); // 'db' is now from the import
        getDoc(orgRef).then(docSnap => {
            if (docSnap.exists()) {
                const orgData = docSnap.data();

                setCurrency(orgData.currency || 'USD');

                if (orgData.automationSettings) {
                    const loadedSettings = orgData.automationSettings;
                    setSettings(prev => ({
                        ...prev,
                        ...loadedSettings,
                        // a1-zerocoerce-9e4b1d76 — `??` not `||`. With `||` a deliberately
                        // stored 0 fell through to the default (0 days became 3) and then
                        // persisted on the next save, silently overriding the choice.
                        // Only absent/null now falls back.
                        reminderStartDays: loadedSettings.reminderStartDays ?? 3,
                        reminderIntervalDays: loadedSettings.reminderIntervalDays ?? 7,
                        lateFeeAmount: loadedSettings.lateFeeAmount ?? 0,
                        lateFeeDays: loadedSettings.lateFeeDays ?? 7,
                        lateFeeType: loadedSettings.lateFeeType || 'Fixed', // <-- NEW: Load saved type
                        fieldTechInvoiceEnabled: loadedSettings.fieldTechInvoiceEnabled || false,
                        // r5-b2b-switches-load-9f2c6a18 — Labor billing switches (Increment 1, inert)
                        trackLaborTime: loadedSettings.trackLaborTime || false,
                        billLaborToCustomer: loadedSettings.billLaborToCustomer || false,
                        // PHASE FO-2 VERIFIER: r1-fo-autosettings-load-5a9c2e71
                        onSiteReviewAssigneeId: loadedSettings.onSiteReviewAssigneeId || '',
                        onSiteReviewAssigneeName: loadedSettings.onSiteReviewAssigneeName || '',
                        // PHASE FO-4 VERIFIER: r1-fo-autosettings-multiload-5a9c2e71
                        // Prefer the saved arrays; if absent but a legacy single
                        // assignee exists, migrate it into a one-element array so
                        // existing configs keep working and upgrade on next save.
                        onSiteReviewAssigneeIds: Array.isArray(loadedSettings.onSiteReviewAssigneeIds)
                            ? loadedSettings.onSiteReviewAssigneeIds
                            : (loadedSettings.onSiteReviewAssigneeId ? [loadedSettings.onSiteReviewAssigneeId] : []),
                        onSiteReviewAssigneeNames: Array.isArray(loadedSettings.onSiteReviewAssigneeNames)
                            ? loadedSettings.onSiteReviewAssigneeNames
                            : (loadedSettings.onSiteReviewAssigneeName ? [loadedSettings.onSiteReviewAssigneeName] : []),
                    }));
                }
            }
            setIsLoading(false);
        }).catch(error => {
            console.error("Error fetching organization data:", error);
            // a1-loadfail-6c2d9a15 — fail closed. Do NOT fall through to the form
            // with defaults in state; that is the path that wipes a live config.
            setLoadError(true);
            setIsLoading(false);
        });
    }, [orgId]); // <-- 'db' is no longer a dependency

    // PHASE FO-2 VERIFIER: r1-fo-autosettings-userloader-5a9c2e71
    // Load org users and filter to invoicing-capable roles for the review
    // assignee picker. Option B: explicit invoicing set (Owner / Admin /
    // Manager / Office Staff) using canonical ROLES constants from
    // permissions.js — excludes Field Technicians AND Sales Consultants,
    // and is immune to the 'Field Tech' vs 'Field Technician' string drift.
    // Query mirrors the established pattern in CustomerFormModal.
    useEffect(() => {
        if (!orgId) return;
        const INVOICING_ROLES = [ROLES.OWNER, ROLES.ADMIN, ROLES.MANAGER, ROLES.OFFICE];
        const usersQ = query(collection(db, 'users'), where('organizationId', '==', orgId));
        getDocs(usersQ).then(snap => {
            const options = snap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(u => INVOICING_ROLES.includes(u.role))
                .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setReviewAssigneeOptions(options);
        }).catch(error => {
            console.error('Error loading review-assignee options:', error);
        });
    }, [orgId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const val = (type === 'checkbox') ? checked : (type === 'number') ? parseFloat(value) : value;
        setSettings(prev => ({
            ...prev,
            [name]: val,
        }));
    };

    // PHASE FO-4 VERIFIER: r1-fo-autosettings-multihandler-5a9c2e71
    // Multi-reviewer: toggle a user in/out of the reviewer arrays. Keeps the
    // ids array and the parallel names array in lockstep. Also keeps the legacy
    // single fields populated with the FIRST selected reviewer so any code path
    // still reading the singular field gets a sensible value (back-compat).
    const handleReviewerToggle = (user) => {
        setSettings(prev => {
            const ids = Array.isArray(prev.onSiteReviewAssigneeIds) ? [...prev.onSiteReviewAssigneeIds] : [];
            const names = Array.isArray(prev.onSiteReviewAssigneeNames) ? [...prev.onSiteReviewAssigneeNames] : [];
            const idx = ids.indexOf(user.id);
            if (idx === -1) {
                ids.push(user.id);
                names.push(user.name || '');
            } else {
                ids.splice(idx, 1);
                names.splice(idx, 1);
            }
            return {
                ...prev,
                onSiteReviewAssigneeIds: ids,
                onSiteReviewAssigneeNames: names,
                // Legacy single fields mirror the first reviewer (or empty).
                onSiteReviewAssigneeId: ids[0] || '',
                onSiteReviewAssigneeName: names[0] || '',
            };
        });
    };

    const handleSave = async () => {
        if (!orgId) { // <-- 'db' check is no longer needed
            alert('Error: Organization not found.'); // Using alert as per existing code
            return;
        }

        // a1-loadfail-6c2d9a15 — belt and braces: never write from a state that
        // was never loaded, even if the form somehow rendered.
        if (loadError) {
            alert('Settings could not be loaded, so they cannot be saved. Please reload the page.');
            return;
        }

        setIsSaving(true);
        const orgRef = doc(db, 'organizations', orgId);
        try {
            // a1-ownedkeys-3f7b2e94 — one dotted path per owned key, never the whole
            // map. `updateDoc(orgRef, { automationSettings: settings })` replaced the
            // entire map; this can only write the 15 keys the form owns and cannot
            // delete a key it never loaded.
            const payload = {};
            AUTOMATION_FIELDS.forEach(key => {
                if (settings[key] !== undefined) payload[`automationSettings.${key}`] = settings[key];
            });
            await updateDoc(orgRef, payload);
            alert('Settings saved successfully!'); // Using alert as per existing code
        } catch (error) {
            console.error("Error saving settings:", error);
            alert('Failed to save settings.'); // Using alert as per existing code
        } finally {
            setIsSaving(false);
        }
    };
    
    if (isLoading) return <p>Loading settings...</p>;

    // a1-loadfail-6c2d9a15 — show an error instead of a form full of defaults.
    if (loadError) {
        return (
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h2 className="text-xl font-bold text-red-700">Couldn't load automation settings</h2>
                <p className="text-gray-600 mt-2">
                    Your current settings could not be read, so they can't be edited right now —
                    saving would overwrite them with defaults. Please reload the page, and if this
                    keeps happening, contact support.
                </p>
            </div>
        );
    }

    return (
        <div className="bg-white p-6 rounded-lg shadow-md space-y-6">
            
            <div>
                {/* --- ADDED THIS HEADER BACK IN --- */}
                <h2 className="text-xl font-bold">Invoice Automation</h2>
                <p className="text-gray-600 mt-1">Automate reminders and late fees for overdue invoices.</p>
            </div>

            {/* --- Reminders Section (Kept from original file) --- */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold">Automated Reminders</h3>
                <label className="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" name="remindersEnabled" checked={settings.remindersEnabled} onChange={handleChange} className="h-4 w-4 rounded" />
                    <span>Enable automated reminder emails for overdue invoices.</span>
                </label>
                {settings.remindersEnabled && (
                    <>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Send first reminder after</label>
                                <div className="flex items-center mt-1">
                                    <input type="number" name="reminderStartDays" value={settings.reminderStartDays} onChange={handleChange} className="w-20 p-2 border rounded-md" />
                                    <span className="ml-2 text-sm text-gray-600">days overdue</span>
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Send follow-ups every</label>
                                <div className="flex items-center mt-1">
                                    <input type="number" name="reminderIntervalDays" value={settings.reminderIntervalDays} onChange={handleChange} className="w-20 p-2 border rounded-md" />
                                    {/* --- FIX: Removed stray 'v' character --- */}
                                    <span className="ml-2 text-sm text-gray-600">days</span>
                                </div>
                            </div>
                        </div>
                        
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reminder Email Body</label>
                            <textarea name="reminderEmailBody" value={settings.reminderEmailBody} onChange={handleChange} rows="6" className="w-full p-2 border rounded-md"></textarea>
                            <p className="text-xs text-gray-500 mt-1">Use placeholders like: {`{customerName}, {invoiceNumber}, {dueDate}, {companyName}`}</p>
                        </div>
                    </>
                )}
            </div>

            {/* --- UPDATED: Late Fees Section --- */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold">Late Fees</h3>
                <label className="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" name="lateFeesEnabled" checked={settings.lateFeesEnabled} onChange={handleChange} className="h-4 w-4 rounded" />
                    <span>Automatically apply late fees to overdue invoices.</span>
                </label>
                {settings.lateFeesEnabled && (
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4"> {/* <-- Changed to 3 cols */}
                            
                            {/* --- NEW: Fee Type Dropdown --- */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Fee Type</label>
                                <select name="lateFeeType" value={settings.lateFeeType} onChange={handleChange} className="mt-1 w-full p-2 border rounded-md bg-white">
                                    <option value="Fixed">Fixed Amount</option>
                                    <option value="Percentage">Percentage</option>
                                </select>
                            </div>
                            
                            {/* --- UPDATED: Dynamic Label --- */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700">
                                    {settings.lateFeeType === 'Fixed' ? `Fee Amount (${currency})` : 'Fee Percentage (%)'}
                                </label>
                                <input type="number" name="lateFeeAmount" value={settings.lateFeeAmount} onChange={handleChange} className="mt-1 w-full p-2 border rounded-md" />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Apply After (Days)</label>
                                <input type="number" name="lateFeeDays" value={settings.lateFeeDays} onChange={handleChange} className="mt-1 w-full p-2 border rounded-md" />
                            </div>
                        </div>
                )}
            </div>
            {/* --- END OF UPDATE --- */}


            {/* --- Field Tech Invoice Section --- */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold">Field Tech Invoicing</h3>
                <label className="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" name="fieldTechInvoiceEnabled" checked={settings.fieldTechInvoiceEnabled} onChange={handleChange} className="h-4 w-4 rounded" />
                    <span>Allow field technicians to invoice a job when they complete it.</span>
                </label>
                {/* techinv-8c3f5a21-b — the old copy said the invoice "will be automatically
                    created and emailed to the customer". That is no longer true and must not
                    be left standing: nothing is emailed automatically now, by anyone. */}
                {settings.fieldTechInvoiceEnabled ? (
                    <p className="text-sm text-gray-500 ml-7">Field techs get a "Complete &amp; Invoice" button on their job screen. The invoice is created but <strong>not sent</strong> — the tech reviews it on their phone and sends it to the customer themselves. Nothing is ever emailed automatically.</p>
                ) : (
                    <p className="text-sm text-gray-500 ml-7">Field techs can only mark jobs complete. Completed jobs with no invoice appear under <strong>Needs Invoicing</strong> on the Jobs page for your office to invoice.</p>
                )}
            </div>

            {/* r5-b2b-switches-ui-4a8d2f60 — Labor billing switches (Increment 1, inert — nothing reads these to bill yet) */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold">Labor Billing</h3>
                <label className="flex items-center space-x-3 cursor-pointer">
                    <input type="checkbox" name="trackLaborTime" checked={settings.trackLaborTime} onChange={handleChange} className="h-4 w-4 rounded" />
                    <span>Track labor time on jobs.</span>
                </label>
                <p className="text-sm text-gray-500 ml-7">Records the hours your team works on each job, for cost and profitability tracking.</p>
                {settings.trackLaborTime && (
                    <label className="flex items-center space-x-3 cursor-pointer ml-7">
                        <input type="checkbox" name="billLaborToCustomer" checked={settings.billLaborToCustomer} onChange={handleChange} className="h-4 w-4 rounded" />
                        <span>Bill tracked labor to customers by default.</span>
                    </label>
                )}
                {settings.trackLaborTime && settings.billLaborToCustomer && (
                    <p className="text-sm text-gray-500 ml-7">Tracked labor will be added to invoices as line items. You'll be able to override this per job.</p>
                )}
                {/* laborbill-one-rule-6c2a9e47 — founder decision 2026-10-04: the job's own choice always wins. */}
                <p className="text-sm text-gray-500 ml-7">A job's own labor-billing setting always overrides this.</p>
            </div>

            {/* PHASE FO-4 VERIFIER: r1-fo-autosettings-multiui-5a9c2e71 */}
            {/* --- On-Site Item Review Section (multi-reviewer) --- */}
            <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold">On-Site Item Review</h3>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">On-site item pricing review goes to:</label>
                    <div className="border rounded-md divide-y max-w-md">
                        {reviewAssigneeOptions.length === 0 ? (
                            <p className="text-sm text-gray-500 p-3">No invoicing-capable team members found.</p>
                        ) : (
                            reviewAssigneeOptions.map(u => {
                                const checked = Array.isArray(settings.onSiteReviewAssigneeIds)
                                    && settings.onSiteReviewAssigneeIds.includes(u.id);
                                return (
                                    <label key={u.id} className="flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-50">
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => handleReviewerToggle(u)}
                                            className="h-4 w-4 rounded"
                                        />
                                        <span className="text-sm text-gray-800">{u.name || 'Unnamed user'}</span>
                                    </label>
                                );
                            })
                        )}
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                        When a technician adds an item in the field, everyone selected here is notified to review pricing before it&apos;s invoiced. Any one of them can mark it reviewed. If no one is selected, reviews go to the account owner.
                    </p>
                </div>
            </div>

            <div className="flex justify-end border-t pt-6">
                <button onClick={handleSave} disabled={isSaving} className="px-6 py-2 bg-blue-600 text-white rounded-md font-semibold hover:bg-blue-700 disabled:bg-gray-400">
                    {isSaving ? 'Saving...' : 'Save Settings'}
                </button>
            </div>
        </div>
    );
};

export default AutomationSettings;